<template>
  <div id="header-search-results">
    <div v-if="searchValue.length < 3">
      Waiting...
    </div>

    <RSpinnerOverlay v-else-if="loading" :loading="loading" :opacity="0">
      <div style="min-height: 10rem" />
    </RSpinnerOverlay>

    <div v-else-if="data.length > 0">
      <div v-for="company in data" :key="company.id" class="search-result py-1">
        <div
          v-if="matchCaseInsensitive(company.human_name, searchValue)"
          class="company"
          @click="handleNavigation(company)"
        >
          <CIcon name="faBriefcase" class="companyIcon" />
          <span :class="{'search-result-link': hasCompany(company.id)}">
            {{ company.human_name }}
          </span>
        </div>
        <div v-for="property in company.properties" :key="`${company.id}-${property.id}`">
          <div
            class="property"
            @click="handleNavigation(company, property)"
          >
            <CIcon name="faBuilding" class="propertyIcon" />
            <span class="search-result-link">{{ property.name }}</span>
            <span class="parentCompany">{{ company.human_name }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      No results, try again.
    </div>
  </div>
</template>

<script>
/**
 * TheheaderSearchResults
 *
 * Displays company/property search results as a nested
 * list. Each item in the list is a link to the
 * respective company/property overview page if the user
 * has permission to view that object.
 *
 * While searching for results, a loading overlay is
 * shown. This component will only be rendered if
 * the search input is non-empty.
 *
 * Events:
 *
 * @on-select: Triggered when user selects a navigation link
 */
import { RSpinnerOverlay } from '@/components/spinners'

export default {
  name: 'TheHeaderSearchResults',
  components: {
    RSpinnerOverlay
  },
  props: {
    loading: {
      type: Boolean,
      required: true
    },
    searchValue: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      required: true
    }
  },
  methods: {
    matchCaseInsensitive (mainString, subString) {
      return RegExp(subString, 'i').test(mainString)
    },
    /**
     * Determines if the target company is available to
     * the current user.
     *
     * @param {Number} cid - company id
     */
    hasCompany (cid) {
      return this.$store.getters['rooof/getCompanyById'](cid) !== undefined
    },
    /**
     * Navigate user to the selected company/property
     * page after storing the object(s) in global state.
     *
     * @param {Object} company
     * @param {Object} [property]
     */
    handleNavigation (company, property = null) {
      // Prevent navigating to company's property list view if we don't have permission
      if (!(property || this.hasCompany(company.id))) {
        return
      }

      if (!property) {
        const location = {
          name: 'PerformanceReport',
          params: {
            cid: company.id
          }
        }
        this.$router.push(location)
        this.$emit('on-select', company.human_name)
      } else {
        const location = {
          name: 'PropertyDetail',
          params: {
            cid: company.id,
            pid: property.id
          }
        }
        this.$router.push(location)
        this.$emit('on-select', property.name)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#header-search-results {
  height: $search-results-height;
  min-width: 201px;
  padding: 1rem;
  overflow-y: auto;
  position: absolute;
  background-color: $white;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1; /* Ensure it's above other content */

  .header-search-info {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .search-result {
    .search-result-link:hover {
      cursor: pointer;
      text-decoration: underline;
    }
    .company {
      font-size: 1rem;
      color: $primary-base;
    }
    .companyIcon {
      margin-right: 0.25rem;
      color: $secondary-base;
      opacity: 0.4;
      margin-bottom: 0.2rem;
    }
    .parentCompany {
      display: inline;
      font-size: 0.85rem;
      color: $gray-500;
      margin-left: 0.2rem;
    }
    .property {
      color: $primary-base;
      font-size: 1rem;
      padding-top: 0.1rem;
      padding-bottom: 0.1rem;
    }
    .propertyIcon {
      margin-right: 0.5rem;
      color: $primary-base;
      opacity: 0.4;
    }
  }
}

@media screen and (min-width: 768px) {
  #header-search-results {
    width: 35rem;
  }
}
</style>
